import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ClockCounterClockwise, User, ShoppingCart, Target, ArrowsClockwise } from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';

const actionIcons = {
  created: <span className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center text-accent text-xs font-bold">+</span>,
  updated: <ArrowsClockwise size={14} weight="bold" className="text-chart-4" />,
  deleted: <span className="w-6 h-6 rounded-full bg-destructive/10 flex items-center justify-center text-destructive text-xs font-bold">&minus;</span>,
};

const entityIcons = {
  client: <User size={14} weight="regular" />,
  sale: <ShoppingCart size={14} weight="regular" />,
  goal: <Target size={14} weight="regular" />,
};

const formatTimestamp = (ts) => {
  const d = new Date(ts);
  const now = new Date();
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const ActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/activity-log?page=${page}&limit=30`, { withCredentials: true });
      setLogs(data.logs || []);
      setTotalPages(data.pages || 1);
    } catch (err) { console.error(err.message);
      // silent
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  if (loading) {
    return <div className="text-center py-12 text-muted-foreground">Loading activity log...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-4">
        <ClockCounterClockwise size={24} weight="regular" />
        <div>
          <div className="text-xl font-semibold tracking-tight">Activity Log</div>
          <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground">AUDIT TRAIL</div>
        </div>
      </div>

      {logs.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-12 text-center text-muted-foreground">
          <ClockCounterClockwise size={48} weight="thin" className="mx-auto mb-4 opacity-50" />
          <p>No activity recorded yet. Actions like creating clients, recording sales, and setting goals will appear here.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-sm">
          <div className="divide-y divide-border">
            {logs.map((log) => (
              <div key={log.id} data-testid={`activity-log-${log.id}`} className="flex items-center gap-4 px-5 py-3.5 hover:bg-muted/50 transition-colors">
                <div className="shrink-0">
                  {actionIcons[log.action] || actionIcons.updated}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm">
                    <span className="font-medium">{log.user_email}</span>
                    <span className="text-muted-foreground"> {log.action} </span>
                    <span className="inline-flex items-center gap-1">
                      {entityIcons[log.entity_type]}
                      <span className="text-muted-foreground">{log.entity_type}</span>
                    </span>
                  </div>
                  {log.details && <div className="text-xs text-muted-foreground mt-0.5">{log.details}</div>}
                </div>
                <div className="text-xs text-muted-foreground shrink-0">{formatTimestamp(log.timestamp)}</div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 p-3 border-t border-border">
              <Button variant="outline" size="sm" className="rounded-sm h-7" disabled={page === 1} onClick={() => setPage(p => p - 1)} data-testid="activity-prev-page">&lt;</Button>
              <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" className="rounded-sm h-7" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="activity-next-page">&gt;</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActivityLog;
